/**
 * Component for customizing quiz button and heading labels.
 *
 * Labels are stored in the quiz's `dictionary` field under specific keys.
 *
 * @module components/quizzes/edit-quiz-labels
 */

import type { Quiz } from '@efdigital/ll-addon-data';
import Component from '@glimmer/component';

import { type EditRecordSignature } from '../edit-record-base.gts';

/**
 * Dictionary keys used for label customization.
 */
const LABEL_KEYS = {
  submitButton: 'submit-button-label',
  changeAnswersButton: 'change-answers-button-label',
  totalsHeading: 'totals-heading-label',
  comparisonHelp: 'comparison-help-text',
} as const;

/**
 * Default label values when not customized.
 */
const DEFAULT_LABELS = {
  submitButton: 'Submit answers',
  changeAnswersButton: 'Change answers',
  totalsHeading: 'Suggested learning',
  comparisonHelp:
    'Compare your results with other groups by clicking the categories above.',
} as const;

type LabelKey = keyof typeof LABEL_KEYS;

export abstract class EditQuizLabelsBase extends Component<
  EditRecordSignature<Quiz>
> {
  /**
   * Get a label value from the dictionary.
   *
   * Returns the customized label if present, or an empty string when unset.
   * The UI uses {@link getPlaceholder} to supply the default label text.
   */
  getLabel = (key: LabelKey): string => {
    const dictionaryKey = LABEL_KEYS[key];
    const dictionary = this.args.item.dictionary as Record<string, string>;
    return dictionary?.[dictionaryKey] ?? '';
  };

  /**
   * Get placeholder text for a label field.
   */
  getPlaceholder = (key: LabelKey): string => {
    return DEFAULT_LABELS[key];
  };

  /**
   * Set a label value in the dictionary.
   */
  setLabel = (key: LabelKey, event: Event) => {
    const target = event.target as HTMLInputElement;
    const dictionaryKey = LABEL_KEYS[key];
    const value = target.value;

    // Ensure dictionary exists
    if (!this.args.item.dictionary) {
      this.args.item.dictionary = {};
    }

    const dictionary = this.args.item.dictionary as Record<string, string>;

    if (value) {
      dictionary[dictionaryKey] = value;
    } else {
      // Remove the key if empty (use default)
      delete dictionary[dictionaryKey];
    }

    // Trigger reactivity by reassigning
    this.args.item.dictionary = { ...dictionary };
  };
}
